import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment as env, environment } from '../../environments/environment';
import { CancellationReason } from '../data-types/cancellation-reason';
import { SearchParams } from '../data-types/search-params';
import { ApiAuthHttpClient } from '../http/ApiAuthHttpClient';
import { SiteSettings } from '../models/site-settings';
import { LocalDigitalOceanIP } from '../shared/model/digital-oceans-ip.model';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root',
})
export class SettingsService {
    private settingsCache$: ReplaySubject<any>;

    constructor(private http: ApiAuthHttpClient, private apiService: ApiService) {}

    /* ===> Cancellations <=== */
    searchCancellationReasons(
        params: SearchParams
    ): Observable<{ totalRecords: number; data: Array<CancellationReason> }> {
        return this.http
            .post(env.apiPath + 'cancellation-reason/search', { params })
            .pipe(map((data: any) => data.data)) as Observable<{
            totalRecords: number;
            data: Array<CancellationReason>;
        }>;
    }

    getCancellationReason(id: number): Observable<any> {
        return this.http.get(env.apiPath + 'cancellation-reason/' + id).pipe(map((data: any) => data.data));
    }

    delCancellationReason(id: number): Observable<any> {
        return this.http.delete(env.apiPath + 'cancellation-reason/' + id).pipe(map((data: any) => data.data));
    }

    createCancellationReason(cancellation: CancellationReason): Observable<any> {
        return this.http.post(env.apiPath + 'cancellation-reason', cancellation).pipe(map((data: any) => data.data));
    }

    updateCancellationReason(cancellation: CancellationReason): Observable<any> {
        return this.http.put(env.apiPath + 'cancellation-reason', cancellation).pipe(map((data: any) => data.data));
    }

    /* ===> Trade service <=== */
    searchService(params): Observable<any> {
        return this.http
            .post(env.apiPath + 'users/company/service/search', { params })
            .pipe(map((data: any) => data.data));
    }

    getService(id): Observable<any> {
        return this.http.get(env.apiPath + 'users/company/service/' + id).pipe(map((data: any) => data.data));
    }

    createService(type): Observable<any> {
        return this.http.post(env.apiPath + 'users/company/service', type).pipe(map((data: any) => data.data));
    }

    updateService(type): Observable<any> {
        return this.http.put(env.apiPath + 'users/company/service/', type).pipe(map((data: any) => data.data));
    }

    deleteService(id): Observable<any> {
        return this.http.delete(env.apiPath + 'users/company/service/' + id).pipe(map((data: any) => data.data));
    }

    getTradeServiceTypes(): Observable<any> {
        return this.http.get(env.apiPath + 'users/company/service-type/all').pipe(map((data: any) => data.data));
    }

    /* ===> Trade service type <=== */
    getTradeTypes(): Observable<any> {
        return this.http.get(env.apiPath + 'users/company/type/all');
    }

    searchServiceType(params): Observable<any> {
        return this.http
            .post(env.apiPath + 'users/company/service-type/search', { params })
            .pipe(map((data: any) => data.data));
    }

    getServiceType(id): Observable<any> {
        return this.http.get(env.apiPath + 'users/company/service-type/' + id).pipe(map((data: any) => data.data));
    }

    updateServiceType(type): Observable<any> {
        return this.http.put(env.apiPath + 'users/company/service-type/', type).pipe(map((data: any) => data.data));
    }

    deleteServiceType(id): Observable<any> {
        return this.http.delete(env.apiPath + 'users/company/service-type/' + id).pipe(map((data: any) => data.data));
    }

    createServiceType(type): Observable<any> {
        return this.http.post(env.apiPath + 'users/company/service-type', type).pipe(map((data: any) => data.data));
    }

    /* ===> Countries <=== */
    searchCountries(params): Observable<any> {
        return this.http.post(env.apiPath + 'countries/search', { params }).pipe(map((data: any) => data.data));
    }

    createCountry(type): Observable<any> {
        return this.http.post(env.apiPath + 'countries', type).pipe(map((data: any) => data.data));
    }

    updateCountry(type): Observable<any> {
        return this.http.put(env.apiPath + 'countries', type).pipe(map((data: any) => data.data));
    }

    deleteCountry(id): Observable<any> {
        return this.http.delete(env.apiPath + 'countries/' + id).pipe(map((data: any) => data.data));
    }

    getCountry(id): Observable<any> {
        return this.http.get(env.apiPath + 'countries/' + id).pipe(map((data: any) => data.data));
    }

    getCountries(): Observable<any> {
        return this.http.get(env.apiPath + 'countries/').pipe(map((data: any) => data.data));
    }

    /* ===> Site Settings <=== */
    getSiteSettings(): Observable<SiteSettings> {
        if (!this.settingsCache$) {
            this.settingsCache$ = new ReplaySubject();
            this.loadSiteSettings();
        }

        return this.settingsCache$;
    }

    private loadSiteSettings() {
        this.http
            .get<{ data: SiteSettings }>(`${env.apiPath}settings/admin`)
            .pipe(
                map(
                    ({ data: siteSettings }) =>
                        ({
                            copyText: siteSettings.copyText,
                            companyNo: siteSettings.companyNo,
                            vatNo: siteSettings.vatNo,
                            telOne: siteSettings.telOne,
                            telTwo: siteSettings.telTwo,
                            emailOne: siteSettings.emailOne,
                            emailTwo: siteSettings.emailTwo,
                            businessName: siteSettings.businessName,
                            addressOne: siteSettings.addressOne,
                            addressTwo: siteSettings.addressTwo,
                            addressThree: siteSettings.addressThree,
                            addressFour: siteSettings.addressFour,
                            addressFive: siteSettings.addressFive,
                            googleMapsURL: siteSettings.googleMapsURL,
                            googleMapsIframe: siteSettings.googleMapsIframe,
                            pinterest: siteSettings.pinterest,
                            facebook: siteSettings.facebook,
                            instagram: siteSettings.instagram,
                            twitter: siteSettings.twitter,
                            deliveryText: siteSettings.deliveryText,
                            competitionRunning: siteSettings.competitionRunning,
                            competitionText: siteSettings.competitionText,
                            competitionTitle: siteSettings.competitionTitle,
                            calculatorHintTonne: siteSettings.calculatorHintTonne,
                            calculatorLengthHint: siteSettings.calculatorLengthHint,
                            deliveryServiceEconomy: siteSettings.deliveryServiceEconomy,
                            deliveryServicePremium: siteSettings.deliveryServicePremium,
                            deliveryPricingMode: siteSettings.deliveryPricingMode || 'default',
                            freeDeliveryThreshold: siteSettings.freeDeliveryThreshold,
                            freeDeliveryThresholdTrade: siteSettings.freeDeliveryThresholdTrade,
                            freeDeliveryThresholdPremiumTrade: siteSettings.freeDeliveryThresholdPremiumTrade,
                            missingImageUrl: siteSettings.missingImageUrl,
                            allowTerminalOnDev: siteSettings.allowTerminalOnDev,
                            tradeDiscountText: siteSettings.tradeDiscountText,
                        } as SiteSettings)
                )
            )
            .subscribe((result) => this.settingsCache$.next(result));
    }

    updateSiteSettings(siteSettings: SiteSettings): Observable<any> {
        siteSettings.id = environment.domainId;

        const dataToSend = {
            siteSettings,
        };
        return this.http.post(`${env.apiPath}settings`, dataToSend).pipe(tap(() => this.loadSiteSettings()));
    }

    getDigitalOceansIPs(): Observable<LocalDigitalOceanIP[]> {
        return this.http
            .get<{ data: LocalDigitalOceanIP[] }>(`${env.apiPath}digital-ocean-ips`)
            .pipe(map(({ data }) => data));
    }

    getDigitalOceansIP(id: number): Observable<LocalDigitalOceanIP> {
        return this.http
            .get<{ data: LocalDigitalOceanIP }>(`${env.apiPath}digital-ocean-ips/${id}`)
            .pipe(map(({ data }) => data));
    }

    createDigitalOceanIP(data: any): Observable<void> {
        return this.http.post<void>(`${env.apiPath}digital-ocean-ips`, data);
    }

    putDigitalOceanIP(id: number, data: any): Observable<void> {
        return this.http.put<void>(`${env.apiPath}digital-ocean-ips/${id}`, data);
    }

    deleteDigitalOceanIP(id: number): Observable<void> {
        return this.http.delete<void>(`${env.apiPath}digital-ocean-ips/${id}`);
    }

    getDataGrid(params) {
        const dataToSend = {
            params,
        };
        return this.http.post(env.apiPath + 'postcode/grid', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateDataGrid(params) {
        return this.http.put(env.apiPath + 'postcode/grid', params, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }
}
